import { useState, type ReactElement } from "react";

import type { BookExposureView } from "../api/client.js";

/**
 * Move money in or out of a bookmaker account.
 *
 * Three jobs, and they are deliberately different kinds rather than one
 * "adjust" catch-all:
 *
 *   Deposit and withdrawal are money actually moving.
 *
 *   Reconcile is for when the derived balance and the real one have drifted -
 *   a promotion, a bonus bet, or a bet placed outside the system. The entered
 *   figure is the TRUE balance and the difference is what gets recorded, so
 *   the gap is written down rather than papered over. A number that goes
 *   quietly wrong is the failure this system keeps hitting.
 *
 * Kind is kept on the row because "why did this account move" is the question
 * asked months later, and a single signed adjustment cannot answer it.
 */
const KINDS = [
  { id: "DEPOSIT", label: "Deposit", hint: "Money in" },
  { id: "WITHDRAWAL", label: "Withdrawal", hint: "Money out" },
  { id: "BONUS", label: "Bonus", hint: "Promo or free bet credited" },
  {
    id: "RECONCILE",
    label: "Correct balance",
    hint: "Enter the real balance; the difference is recorded",
  },
] as const;

type Kind = (typeof KINDS)[number]["id"];

export function BookFunds({
  books,
  onSubmit,
}: {
  readonly books: readonly BookExposureView[];
  readonly onSubmit: (input: {
    bookmaker: string;
    kind: Kind | "ADJUSTMENT";
    amountCents: number;
    note: string | null;
  }) => void;
}): ReactElement {
  const [bookmaker, setBookmaker] = useState(books[0]?.bookmaker ?? "");
  const [kind, setKind] = useState<Kind>("DEPOSIT");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");

  const selected = books.find((book) => book.bookmaker === bookmaker);
  const entered = Math.round(Number(amount) * 100);
  const valid = Number.isFinite(entered) && entered !== 0 && bookmaker !== "";

  // A reconcile is entered as the true balance, so the amount recorded is the
  // gap between that and what we derived.
  const delta =
    kind === "RECONCILE" && selected !== undefined
      ? entered - selected.availableCents
      : kind === "WITHDRAWAL"
        ? -Math.abs(entered)
        : Math.abs(entered);

  return (
    <form
      className="card book-funds"
      aria-label="Adjust book funds"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid || delta === 0) return;
        onSubmit({
          bookmaker,
          kind,
          amountCents: delta,
          note: note.trim() === "" ? null : note.trim(),
        });
        setAmount("");
        setNote("");
      }}
    >
      <div className="section-heading">
        <h3>Add or remove funds</h3>
      </div>
      <label>
        <span>Book</span>
        <select
          value={bookmaker}
          onChange={(event) => setBookmaker(event.target.value)}
        >
          {books.map((book) => (
            <option key={book.bookmaker} value={book.bookmaker}>
              {book.bookmaker}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>Kind</span>
        <select
          value={kind}
          onChange={(event) => setKind(event.target.value as Kind)}
        >
          {KINDS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>{kind === "RECONCILE" ? "Real balance" : "Amount"}</span>
        <input
          type="number"
          step="0.01"
          inputMode="decimal"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </label>
      <label>
        <span>Note</span>
        <input
          type="text"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder={
            kind === "RECONCILE" ? "Vibe bet, promo, fee" : "Optional"
          }
        />
      </label>
      <p className="muted">
        {KINDS.find((option) => option.id === kind)?.hint}
        {kind === "RECONCILE" && selected !== undefined && valid
          ? ` · recording ${delta >= 0 ? "+" : "-"}$${(Math.abs(delta) / 100).toFixed(2)}`
          : ""}
      </p>
      <button className="button primary" type="submit" disabled={!valid}>
        Record
      </button>
    </form>
  );
}
